import { NavigateFunction } from 'react-router';

import { Button } from '@/components/ui/button';
import { NoAssetsFound as NoAssetsFoundIcon } from '@/components/icons';
import { NoDataProps } from '@/components/legacy-ui/NoData';
import {
  createAllEmptyConfig,
  createFilteredEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/legacy-ui/utils/createNoDataConfig';

interface AssetNoDataConfigParams {
  isQueryError: boolean;
  isAllEmpty: boolean;
  onResetQuery: () => void;
  navigate: NavigateFunction;
  /** Whether filters are currently active */
  hasActiveFilters?: boolean;
  /** Callback to clear all filters */
  onClearFilters?: () => void;
  /** Callback when "Get Started" button is clicked (for first-time users) */
  onGetStartedClick?: () => void;
}

/**
 * Generates NoData configuration for asset table empty states
 *
 * Handles three scenarios:
 * 1. Query Error - Invalid query parameters (e.g., bad orderBy or filters)
 * 2. All Empty - First-time user with no assets
 * 3. Filtered Empty - User has assets but current filters return none
 *
 * @param params - Configuration parameters
 * @returns NoData component props
 */
export const getAssetNoDataConfig = ({
  isQueryError,
  isAllEmpty,
  onResetQuery,
  navigate: _navigate,
  hasActiveFilters,
  onClearFilters,
  onGetStartedClick,
}: AssetNoDataConfigParams): NoDataProps => {
  const icon = <NoAssetsFoundIcon className={NO_DATA_ICON_CLASS} />;

  // Query Error State
  if (isQueryError) {
    return {
      icon,
      title: 'Query Error',
      description: (
        <p className="text-chariot-text-secondary text-sm">
          There&apos;s an issue with the current query parameters (possibly
          orderBy or filters).
        </p>
      ),
      action: (
        <Button
          onClick={onResetQuery}
          variant="secondary"
          size="sm"
        >
          Reset Query Parameters
        </Button>
      ),
    };
  }

  // All Empty State (First time user)
  if (isAllEmpty) {
    return createAllEmptyConfig({
      entityName: 'No Assets to Show Yet',
      icon,
      description: (
        <p>Choose how you&apos;d like to populate your attack surface.</p>
      ),
      onGetStarted: onGetStartedClick,
    });
  }

  // Filtered Empty State - shows Clear Filters button for consistency with other sections
  return createFilteredEmptyConfig({
    entityName: 'Assets',
    icon,
    hasActiveFilters: hasActiveFilters ?? false,
    onClearFilters: onClearFilters ?? (() => {}),
  });
};
